// Japanese (日本語) dictionary. Implements MktDict — same shape as en.ts.
// Numbers (47/10/7/v4/60+/9) and proper nouns / brand names stay as-is.

import type { MktDict } from "./en";

export const ja: MktDict = {
  nav: {
    items: {
      pricing: "料金",
      examples: "事例",
      templates: "ライブラリ",
      design: "デザイン",
      docs: "ドキュメント",
      developers: "開発者",
      blog: "ブログ",
      about: "概要",
    },
    openStudio: "スタジオを開く →",
    themeToggle: "テーマ切り替え",
    language: "言語",
    status: "ステータス",
    signIn: "ログイン",
    menu: "メニュー",
  },

  footer: {
    tagline: "意図とソース資料を、生きたアプリやドキュメントに変えます。",
    groups: {
      product: {
        title: "製品",
        links: {
          pricing: "料金",
          templates: "ライブラリ",
          docs: "ドキュメント",
          designSystem: "デザインシステム",
        },
      },
      company: {
        title: "会社",
        links: { about: "概要", blog: "ブログ", contact: "お問い合わせ" },
      },
      compare: {
        title: "比較",
        links: { framer: "vs Framer", wix: "vs Wix", v0: "vs V0" },
      },
      legal: {
        title: "法務",
        links: {
          privacy: "プライバシー",
          terms: "利用規約",
          security: "セキュリティ",
        },
      },
    },
    copyright: "coogo · PADO Software Design L.L.C.",
    location: "ドバイ, UAE",
  },

  homeFooter: {
    groups: {
      product: {
        title: "製品",
        links: {
          templates: "ライブラリ",
          designSystem: "デザインシステム",
          pricing: "料金",
          studio: "スタジオ",
          howItWorks: "仕組み",
        },
      },
      resources: {
        title: "リソース",
        links: {
          documentation: "ドキュメント",
          blog: "ブログ",
          apiReference: "APIリファレンス",
          templates: "ライブラリ",
        },
      },
      company: {
        title: "会社",
        links: {
          aboutUs: "会社概要",
          contact: "お問い合わせ",
          careers: "採用情報",
        },
      },
      legal: {
        title: "法務",
        links: { terms: "利用規約", privacy: "プライバシー", refund: "返金" },
      },
    },
  },

  hero: {
    badge: "coogo Studio",
    h1: { line1: "生きたアプリ。", line2: "生きたドキュメント", accent: "。" },
    subhead:
      "作りたいもの、変えたいものを説明してください。coogoはソース資料と一緒に作業し、見える変更案を出し、アプリ・ドキュメント・レポートをレビュー、共有、エクスポートできる状態に保ちます。",
    promptLines: [
      "ソース、レビューメモ、エクスポート状態がつながるキャンペーンレポート…",
      "クリニック予約アプリ — 顧客が予約し、スタッフにSMS通知…",
      "月額サブスクとダークモードのメンバー限定ポッドキャスト…",
      "紹介リーダーボード付きのキャットシッターサービス待機リスト…",
    ],
    inputPlaceholder:
      "オンボーディングアプリ、資料変換、レポート修正を coogo に依頼…",
    generate: "coogo に依頼",
    chips: ["App", "Document", "Report", "レビュー"],
  },

  why: {
    label: "01 / product",
    h2: "ユーザーの意図と coogo agent が同じ作業空間で動きます。",
    intro:
      "coogoは静的なページ生成器ではありません。意図とソース資料を、レビューでき、変更でき、信頼できる成果物に変えます。",
    card1: {
      chip: "applications",
      h3: "アプリケーションは生き続けます。",
      body: "プロダクト画面、社内ツール、モバイル業務フローを依頼できます。coogoは実行中のアプリをStudioで開き、次の依頼も同じ見える成果物に反映します。",
      annLeft: "web · cogo.xyz",
      annMid: "← same JSON →",
      annRight: "mobile · iOS · Android",
    },
    card2: {
      chip: "documents",
      h3: "ドキュメントは編集可能なままです。",
      body: "ファイルをアップロードし、構造を依頼し、静かな上書きではなく提案と承認で修正します。ドキュメントは一度きりのAI下書きではなく、作業中の成果物として残ります。",
    },
    card3: {
      chip: "design system",
      h3: "レポートはソースに接続されます。",
      body: "チャート、表、テキスト、エクスポートはソース範囲と検証確認に接続され、共有前に信頼状態が見えます。",
      link: "デザインシステムを見る →",
    },
  },

  how: {
    label: "02 / workflow",
    h2: "依頼し、レビューし、承認し、エクスポートします。",
    intro:
      "基本ループはシンプルです。coogoに依頼し、成果物を確認し、提案された変更を決めてから、公開・共有・エクスポートします。",
    steps: [
      {
        n: "01",
        title: "coogo に依頼",
        body: "望む結果を説明するか、ソース資料を添付します。依頼はURLに出さず、Studio handoffにつながります。",
        caption: "意図を保持",
      },
      {
        n: "02",
        title: "成果物を見る",
        body: "アプリ、ドキュメント、レポートが中央の作業面になります。ソースと確認情報は必要な時に見られます。",
        caption: "成果物優先",
      },
      {
        n: "03",
        title: "決める",
        body: "coogoは変更をわかりやすく提案します。成果物が変わる前に、使う、修正する、現在のままにするを選びます。",
        caption: "使う / 修正 / 維持",
      },
      {
        n: "04",
        title: "共有またはエクスポート",
        body: "アプリを公開し、レビューリンクを共有し、ドキュメントやレポートを対象別の準備状態と一緒にエクスポートします。",
        caption: "公開 / 共有 / エクスポート",
      },
    ],
  },

  templates: {
    label: "03 / launch lanes",
    h2: "手元にある仕事の種類から始めます。",
    intro:
      "coogo homeはStudioと同じ制作言語で始めます: application, document, report, revision。",
    all: "ライブラリを開く",
    live: "準備済み",
    preview: "開始",
    webMobile: "coogo Studio",
    items: [
      { name: "アプリケーションを作る", cat: "生成アプリ作業空間" },
      { name: "ドキュメントを変換", cat: "DOCX, HWPX, PPTX, XLSX" },
      { name: "レポートを作成", cat: "ソース接続の表とチャート" },
      { name: "既存作業を修正", cat: "提案と承認のループ" },
    ],
  },

  builtOn: {
    label: "04 / collaboration",
    h2: "人とagentが同じ成果物上で働きます。",
    intro:
      "線を中心にしたインターフェースで、ユーザー、ソース、coogo、成果物、レビュー状態がつながっていることを示します。内部の仕組みは初期画面に出しません。",
    taglines: ["依頼", "理解", "ソース", "成果物", "検証"],
  },

  faq: {
    label: "05 / questions",
    h2: "最初に理解してほしいこと。",
    items: [
      {
        q: "coogoはアプリ用ですか、ドキュメント用ですか？",
        a: "両方です。生きたアプリケーションと生きたドキュメントが約束です。coogoは同じStudioループで作成、変更、レビュー、共有、エクスポートします。",
      },
      {
        q: "ランディング入力の後は何が起きますか？",
        a: "coogoは依頼をStudio handoffに保存し、作業空間を開き、プロンプトをURLに入れず会話を成果物に接続します。",
      },
      {
        q: "agentが承認済みの作業を黙って変えますか？",
        a: "いいえ。制作画面の契約は提案が先です。coogoは変更を説明し、対象を示し、必要な場合は使う、修正、現在のままを待ちます。",
      },
      {
        q: "共有した成果物は公開後どう管理しますか？",
        a: "ソースパケット、レビュー履歴、エクスポート状態を一緒に保持し、共有後も改善を続けられます。",
      },
      {
        q: "ネイティブファイルにエクスポートできますか？",
        a: "はい。ドキュメントとレポートはDOCX、HWPX、PPTX、XLSX、PDFなどの形式を対象にできます。",
      },
      {
        q: "最初の画面は何を伝えるべきですか？",
        a: "coogoが会話と見える成果物を通じて、アプリ、ドキュメント、レポートを作成または変更できることです。",
      },
    ],
  },

  cta: {
    label: "06 / start",
    h2: { pre: "生かしたい仕事を ", accent: "coogoへ。" },
    body: "依頼、ソースファイル、粗いレポート案から始めてください。Studioが成果物と次の判断へつなげます。",
    start: "Studioを開く",
    browse: "ライブラリを見る",
    chips: ["application", "document", "report", "approval loop"],
  },

  pricing: {
    label: "§ PRICING",
    h1: { line1: "ひとつの価格で、", line2: "生きた成果物を。" },
    subhead:
      "アプリ、ドキュメント、レポートをレビュー、共有、エクスポートの流れまで含めて提供します。",
    billing: { monthly: "月額", annual: "年額 — 20%オフ" },
    perMonth: "/月",
    customPrice: "カスタム",
    contactForPricing: "料金はお問い合わせ",
    billedAnnually: "年額請求",
    billedMonthly: "月額請求",
    tiers: [
      {
        name: "Hobby",
        tag: "永久無料",
        cta: "無料で始める",
        bullets: [
          "共有成果物 1個",
          "cogo.xyz サブドメイン",
          "コミュニティサポート",
          "100 MB ストレージ",
        ],
      },
      {
        name: "Creator",
        tag: "一番人気",
        cta: "14日間無料トライアル",
        bullets: [
          "共有成果物 10個",
          "カスタムドメイン",
          "レビューワークフロー",
          "5 GB ストレージ",
          "メールサポート",
        ],
      },
      {
        name: "Studio",
        tag: "チーム向け",
        cta: "トライアル開始",
        bullets: [
          "共有成果物 50個",
          "チームワークスペース (5席)",
          "優先サポート",
          "25 GB ストレージ",
          "高度な分析",
        ],
      },
      {
        name: "Enterprise",
        tag: "カスタム",
        cta: "お問い合わせ",
        bullets: [
          "無制限の成果物",
          "SSO & SAML",
          "SLA",
          "専任担当",
          "カスタム契約",
        ],
      },
    ],
    faqLabel: "FAQ",
    faqTitle: "始める前の、よくある質問",
    faq: [
      {
        q: "本当にすべてのプランでモバイルアプリが手に入りますか？",
        a: "はい。すべてのアプリにレスポンシブなウェブビルドとiOS/Android対応のネイティブラッパーが付きます。追加手順はありません。",
      },
      {
        q: "自分のドメインは使えますか？",
        a: "Creator以上なら可能です。ドメインをcoogoに接続すれば、SSLは自動で処理されます。",
      },
      {
        q: "ダウングレードするとどうなりますか？",
        a: "新しいプランを超えるアプリは削除されず、読み取り専用に切り替わります。いつでもアップグレードすれば再有効化されます。",
      },
      {
        q: "成果物の共有ごとに追加料金はありますか？",
        a: "いいえ。プランは共有成果物、AIクレジット、ストレージ、チーム機能に基づきます。あなたの成果物から手数料を取りません。",
      },
    ],
  },

  about: {
    label: "§ ABOUT",
    h1: { line1: "私たちが使いたかった", line2: "ツールを作ります。" },
    intro:
      "coogoはソウルの週末プロジェクトとして始まりました。友人たちがウェブアプリに4万ドルを払い、それをまたモバイルアプリとして作り直さなければならないのを見るのにうんざりしていたのです。だから機械に両方を一度にやらせました。2度目の作り直しも、2つ目の契約も、2つ目の請求書もありません。",
    timeline: [
      {
        year: "2023",
        label: "開始",
        desc: "エンジニア2人、コーヒーショップ1軒。",
      },
      {
        year: "2024",
        label: "パブリックベータ",
        desc: "8週間で1.2万人が登録。",
      },
      {
        year: "2025",
        label: "現在",
        desc: "数千のアプリを公開、ソウル + SFに40人。",
      },
    ],
    teamLabel: "§ TEAM",
    teamTitle: "機械の裏にいる人たち",
    team: [
      { name: "Chan", role: "共同創業者 · CEO" },
      { name: "Min", role: "共同創業者 · CTO" },
      { name: "Ha-eun", role: "デザイン責任者" },
      { name: "Paul", role: "グロース責任者" },
    ],
    contactLabel: "§ CONTACT",
    contactTitle: "こんにちは。",
    contactIntro: "取材、提携、ただ話したいだけ — すべてのメールに返信します。",
    contactGeneral: "一般",
    contactPress: "取材",
    contactEnterprise: "エンタープライズ",
    formName: "名前",
    formNamePlaceholder: "お名前",
    formEmail: "メール",
    formEmailPlaceholder: "you@email.com",
    formMessage: "メッセージ",
    formMessagePlaceholder: "何を作っているか教えてください…",
    formSend: "メッセージを送る",
  },

  templatesPage: {
    label: "§ GALLERY",
    h1: "本物から始めよう。",
    subhead:
      "アプリ、ドキュメント、レポート、修正の出発点を選び、coogoと続けましょう。",
    cats: [
      "レポート",
      "ドキュメント",
      "分析",
      "ヘルス",
      "Starter",
      "SaaS",
      "管理",
      "生産性",
    ],
  },

  blog: {
    label: "§ JOURNAL",
    h1: "フィールドノート。",
    subhead: "ビルダー、メイカー、ソフトウェアについて声に出して考える。",
    featuredSuffix: "注目",
    featuredExcerpt:
      "10年間、小さなビジネスが2つのアプリにお金を払い、結局ちゃんと動くのは1つだけという光景を見てきました。なぜ私たちが別の機械を作り始めたのかを説明するために書いた記事です。",
    minRead: "分",
    posts: [
      { t: "なぜcoogoを作ったのか", tag: "Manifesto" },
      { t: "初日からのネイティブモバイルを紹介", tag: "Product" },
      { t: "coogoを形づくる3つの信念", tag: "Philosophy" },
      {
        t: "一度編集すればどこでも反映：バインディングの仕組み",
        tag: "Engineering",
      },
      { t: "小さなアプリへのラブレター", tag: "Essay" },
      { t: "Starterスポットライト：ソース連携レポート", tag: "Showcase" },
    ],
  },

  docs: {
    searchPlaceholder: "ドキュメントを検索",
    sections: [
      {
        h: "はじめに",
        items: ["概要", "最初の成果物を作る", "共有または公開", "次に進む場所"],
      },
      {
        h: "ビルド",
        items: [
          "coogo JSON 概要",
          "スキーマリファレンス",
          "ブロック",
          "イベント & フロー",
          "式",
        ],
      },
      {
        h: "ACTIONFLOW",
        items: [
          "概要",
          "実行モード",
          "アクションカタログ (60+)",
          "Validator ループ",
        ],
      },
      {
        h: "STUDIO",
        items: [
          "概要",
          "ダッシュボード",
          "共有",
          "IDE ワークスペース",
          "テーマエディタ",
        ],
      },
      {
        h: "デザインシステム",
        items: ["概要", "トークン", "ソースモデル", "システムの拡張"],
      },
      {
        h: "リファレンス",
        items: [
          "デザインシステムページ",
          "コンポーネント (作業中)",
          "ソースモデル (作業中)",
          "生成 (作業中)",
          "デプロイ (作業中)",
          "API リファレンス (作業中)",
        ],
      },
    ],
    centerLabel: "はじめに",
    h1: "概要",
    intro:
      "coogoは、アプリ、ドキュメント、レポートを同じ作業空間で作り、検討し、共有するためのStudioです。やりたいこととソースを渡すと、coogoが見える成果物と次の判断に変えます。",
    calloutTitle: "coogoは初めてですか？",
    calloutBody:
      " 一番早いのは、作りたい成果物と手元のソースをひとつの依頼にまとめることです。近いStarterがあれば、それを出発点にできます。",
    threeTitle: "知っておくべき3つ",
    three: [
      {
        title: "依頼とソースを一緒に扱います。",
        body: " ファイル、メモ、既存画面を同じ文脈に置き、何を作るかを見失わないようにします。",
      },
      {
        title: "成果物はレビュー可能です。",
        body: " アプリ、ドキュメント、レポートは見える形で生成され、変更点を確認してから使えます。",
      },
      {
        title: "必要なときだけ開発者向け詳細へ進みます。",
        body: " 通常の作業画面では内部モデルを隠し、検証や構造が必要な場面でだけ参照できます。",
      },
    ],
    quickStartTitle: "クイックスタート",
    quickStart: {
      c1: "# 1. 依頼を書く",
      c2: "# 2. ソースを追加する",
      c3: "# 3. 成果物をレビューして共有する",
    },
    next: "次へ",
    nextLink: "最初の成果物を作る →",
    onThisPage: "このページの内容",
    toc: ["概要", "知っておくべき3つ", "クイックスタート"],
  },

  designSystem: {
    sidebar: {
      groups: { foundations: "基礎", system: "システム", contract: "契約" },
      items: {
        overview: "概要",
        philosophy: "哲学",
        architecture: "アーキテクチャ",
        tokens: "トークン",
        components: "コンポーネント",
        patterns: "パターン",
        rules: "Tailwind ルール",
        ontology: "ソースモデル",
        schema: "JSON Schema",
        prompt: "LLM プロンプト",
        samples: "サンプル",
      },
      source: "ソース",
    },
    heroChip: "coogo デザインシステム · v4",
    heroH1: { line1: "閉じた語彙、", accent: "開いた表現。" },
    heroSubtitle:
      "マーケティング、IDE、レビュー、ダッシュボード、チャット — すべての画面をひとつの語彙で。",
    sections: {
      philosophy: {
        name: "哲学",
        h2: "語彙を閉じ、表現を開く。",
        sub: "coogo DSの第一原則。",
      },
      architecture: {
        name: "アーキテクチャ",
        h2: "ひとつのソース、6つの成果物。",
        sub: "YAMLで編集。残りはすべて生成。",
      },
      tokens: {
        name: "トークン",
        h2: "47個のトークン、ドリフト0。",
        sub: "47個のトークン、ドリフト0。",
      },
      components: {
        name: "コンポーネント",
        h2: "ひとつのボタン、3つの世界。",
        sub: "ひとつのボタン、3つの世界。",
      },
      patterns: {
        name: "パターン",
        h2: "10個のアーキタイプ、無限のアプリ。",
        sub: "10個のアーキタイプ、無限のアプリ。",
      },
      rules: {
        name: "Tailwind ルール",
        h2: "骨格はclassName、見た目はvariant。",
        sub: "骨格はclassName、見た目はvariant。",
      },
      ontology: {
        name: "ソースモデル",
        h2: "ソースと制約を、見える契約に。",
        sub: "成果物が何に基づいているかを確認できます。",
      },
      schema: {
        name: "JSON Schema",
        h2: "5つのファイルが1つのブロックを宣言する。",
        sub: "5つのファイルが1つのブロックを宣言する。",
      },
      prompt: {
        name: "LLM プロンプト",
        h2: "ブリーフ → 生成 → 検証 → リトライ。",
        sub: "ブリーフ → 生成 → 検証 → リトライ。",
      },
      samples: {
        name: "サンプル",
        h2: "ひとつの語彙、5つのブロック。",
        sub: "同じ語彙、5つのブロック。",
      },
    },
  },

  pricingPage: {
    h1: "料金",
    subhead: "ビジネスを本気で考えるクリエイターのために。",
    currencyNote: "すべてのプランは UAE ディルハム（AED）で請求されます。",
    startFree: "無料で始める",
    subscribeMonthly: "月額で登録",
    subscribeYearly: "年額で登録",
    perMonth: "/ 月",
    perYear: "/ 年",
    billingMonthly: "月額",
    billingYearly: "年額",
    yearlySaveHint: "2ヶ月無料",
    yearlySaveLabel: "年間{amount}お得（{pct}%オフ）",
    reassureFree: "クレジットカード不要",
    reassurePaid: "いつでも解約可能 · 安全な決済",
    errPayment: "決済に失敗しました",
    errNoUrl: "決済URLを取得できませんでした",
    errNotAuth: "認証されていません",
    plans: {
      free: {
        title: "無料",
        description:
          "最適な対象：最初のプロダクトアイデアを検証するクリエイター。",
        features: [
          "共有成果物 1個",
          "月 500 AIクレジット",
          "基本レビュー履歴",
          "標準サポート",
        ],
        badge: "",
      },
      creator: {
        title: "Creator",
        description: "最適な対象：複数の成果物を共有するチーム。",
        features: [
          "共有成果物 5個",
          "月 5,000 AIクレジット",
          "レビュー履歴",
          "カスタムドメイン対応",
          "基本分析",
        ],
        badge: "一番人気",
      },
      business: {
        title: "Business",
        description: "最適な対象：多くの成果物を運用する組織。",
        features: [
          "共有成果物 30個",
          "月 20,000 AIクレジット",
          "チームレビュー履歴",
          "高度な分析",
          "優先サポート",
        ],
        badge: "",
      },
    },
    faq: {
      title: "料金に関する質問",
      items: [
        {
          q: "請求通貨は何ですか？",
          a: "すべてのプランは UAE ディルハム（AED）で請求されます。表示価格がそのまま請求額で、隠れた為替手数料はありません。",
        },
        {
          q: "月額と年額の違いは？",
          a: "年額プランなら2ヶ月分が無料 — 月々払いと比べて約17%お得です。上のトグルで月額／年額を切り替えられます。",
        },
        {
          q: "無料トライアルはありますか？",
          a: "期間限定のトライアルはありません。無料プランはクレジットカード不要でずっと使えるので、まず共有成果物を作り、必要になったときだけアップグレードできます。",
        },
        {
          q: "いつでも解約できますか？",
          a: "はい。アカウント設定からいつでも解約できます — 現在の請求期間の終わりまでプランは有効で、次の期間は請求されません。サブスクリプションは返金不可です。返金・キャンセルポリシーをご確認ください。",
        },
        {
          q: "共有した成果物は公開後どう管理しますか？",
          a: "各成果物はソースパケット、レビュー履歴、エクスポート状態を一緒に保持するため、共有後もチームで改善できます。",
        },
        {
          q: "独自ドメインは使えますか？",
          a: "はい。アプリはデフォルトで無料の name.cogo.xyz アドレスに公開され、設定パネルから独自ドメインを接続して DNS を当社のエッジに向けられます。",
        },
        {
          q: "AIクレジットとは？",
          a: "AIクレジットは、アプリの生成・編集などAI作業に使う月間の利用枠です。プランごとに毎月、無料 500、Creator 5,000、Business 20,000 クレジットが含まれます。",
        },
      ],
    },
  },

  consent: {
    title: "決済の同意",
    description: "お進みになる前に、ポリシーをご確認のうえ同意してください。",
    agreeIntro: "",
    and1: "と",
    and2: "に同意し、",
    suffix: "を読んだことを確認します。",
    termsLabel: "利用規約",
    privacyLabel: "プライバシーポリシー",
    refundLabel: "返金・キャンセルポリシー",
    cancel: "キャンセル",
    processing: "処理中...",
    proceed: "決済に進む",
    close: "閉じる",
  },
};
